/**
 * The on-device diagnostics log (ADR-0024): a bounded ring buffer in its own IndexedDB database, so it never
 * touches the event log or its schema, and the watchdog worker can write to it while the page is frozen.
 * Plain IndexedDB (no Dexie) so the same code runs in a Worker.
 */
export type Level = 'debug' | 'info' | 'warn' | 'error';
export interface Entry {
  /** epoch ms */
  t: number;
  /** this app run */
  sid: string;
  level: Level;
  /** sync, session, app, config, perf, hang, error, … */
  area: string;
  event: string;
  data?: Record<string, string | number | boolean | null>;
}

export const DIAG_DB = 'repwise-diag';
const STORE = 'log';
/** About 2 MB at typical entry sizes. */
export const MAX_ENTRIES = 5000;

let opening: Promise<IDBDatabase | null> | null = null;
export function openDiag(): Promise<IDBDatabase | null> {
  if (opening) return opening;
  opening = new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') return resolve(null);
      const r = indexedDB.open(DIAG_DB, 1);
      r.onupgradeneeded = () => r.result.createObjectStore(STORE, { autoIncrement: true });
      r.onsuccess = () => {
        r.result.onversionchange = () => r.result.close(); // a wipe (J8) can delete us
        resolve(r.result);
      };
      r.onerror = () => resolve(null);
      r.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return opening;
}

const done = (tx: IDBTransaction) => new Promise<void>((res) => ((tx.oncomplete = () => res()), (tx.onerror = () => res()), (tx.onabort = () => res())));

export async function addEntries(entries: readonly Entry[]): Promise<void> {
  if (!entries.length) return;
  const db = await openDiag();
  if (!db) return;
  try {
    const tx = db.transaction(STORE, 'readwrite');
    const os = tx.objectStore(STORE);
    for (const e of entries) os.add(e);
    await done(tx);
    await trim(db);
  } catch {
    /* diagnostics must never break the app */
  }
}

let writes = 0;
async function trim(db: IDBDatabase): Promise<void> {
  if (++writes % 20 !== 0) return; // check the size now and then, not on every flush
  const tx = db.transaction(STORE, 'readwrite');
  const os = tx.objectStore(STORE);
  const count = await new Promise<number>((res) => {
    const q = os.count();
    q.onsuccess = () => res(q.result);
    q.onerror = () => res(0);
  });
  if (count > MAX_ENTRIES) {
    let drop = count - MAX_ENTRIES;
    const c = os.openCursor();
    c.onsuccess = () => {
      const cur = c.result;
      if (cur && drop-- > 0) {
        cur.delete();
        cur.continue();
      }
    };
  }
  await done(tx);
}

export async function allEntries(): Promise<Entry[]> {
  const db = await openDiag();
  if (!db) return [];
  return new Promise((res) => {
    try {
      const q = db.transaction(STORE, 'readonly').objectStore(STORE).getAll();
      q.onsuccess = () => res(q.result as Entry[]);
      q.onerror = () => res([]);
    } catch {
      res([]);
    }
  });
}

export async function clearEntries(): Promise<void> {
  const db = await openDiag();
  if (!db) return;
  const tx = db.transaction(STORE, 'readwrite');
  tx.objectStore(STORE).clear();
  await done(tx);
}

/** J8 "Remove data from this device": the diagnostics go too. */
export async function deleteDiag(): Promise<void> {
  const db = await openDiag();
  db?.close();
  opening = null;
  await new Promise<void>((res) => {
    try {
      const r = indexedDB.deleteDatabase(DIAG_DB);
      r.onsuccess = r.onerror = r.onblocked = () => res();
    } catch {
      res();
    }
  });
}
